import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  Copy,
  ExternalLink,
  InfoIcon,
  RefreshCw,
  Search,
  SkipForward,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';

import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { copyToClipboard } from '~shared/utils/clipboard';

import { useGetFormConfigs } from '../api/get-form-configs';
import { useGetFormSubmissions } from '../api/get-form-submissions';
import {
  useRetryFormSubmission,
  useSkipFormSubmission,
} from '../api/submission-actions';
import { formatCrmResolutionLabel } from '../lib/crm-resolution-labels';
import { formatFormTypeLabel } from '../lib/form-type-labels';
import type { FormSubmission } from '../api/types';
import { FormSubmissionDetailsModal } from './form-submission-details-modal';
import { SyncStatusBadge } from './sync-status-badge';

const columnHelper = createColumnHelper<FormSubmission>();

const SYNC_STATUS_OPTIONS = ['pending', 'synced', 'failed', 'skipped'];

function crmContactUrl(locationId: string | undefined, contactId: string) {
  return `https://app.enrollio.ai/v2/location/${locationId}/contacts/detail/${contactId}`;
}

export function FormSubmissionsTable() {
  const { formatInStudioTz } = useStudioDateFormatter();
  const [formTypeFilter, setFormTypeFilter] = useState<string>('All');
  const [syncStatusFilter, setSyncStatusFilter] = useState<string>('All');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<FormSubmission | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: getStoredPageSize(20),
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const { data: configsData } = useGetFormConfigs();
  const availableFormTypes = useMemo(
    () => (configsData ?? []).map((c) => c.formType),
    [configsData],
  );

  const params = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    formType: formTypeFilter !== 'All' ? formTypeFilter : undefined,
    syncStatus: syncStatusFilter !== 'All' ? syncStatusFilter : undefined,
    search: search.trim() || undefined,
    from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
  };

  const { data, isFetching, error } = useGetFormSubmissions(params);
  const rows = data?.data ?? [];
  const totalCount = data?.total ?? 0;

  const retryMutation = useRetryFormSubmission();
  const skipMutation = useSkipFormSubmission();

  const handleRetry = (row: FormSubmission) => {
    retryMutation.mutate(
      { id: row._id },
      {
        onSuccess: () => toast.success('Sync retry queued'),
        onError: () =>
          toast.error('Could not queue a retry for this submission.'),
      },
    );
  };

  const handleSkip = (row: FormSubmission) => {
    skipMutation.mutate(
      { id: row._id },
      {
        onSuccess: () => toast.success('Sync marked as skipped'),
        onError: () =>
          toast.error('Could not mark this submission as skipped.'),
      },
    );
  };

  const handleCopyContact = async (row: FormSubmission) => {
    const text = `${row.parent.firstName} ${row.parent.lastName} <${row.parent.email}> ${row.parent.phone}`;
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success('Contact info copied');
    } else {
      toast.error('Copy failed');
    }
  };

  const cols = useMemo(
    () => [
      columnHelper.accessor('submittedAt', {
        id: 'submittedAt',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Submitted'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          try {
            return (
              <div className='px-4'>
                {formatInStudioTz(value, "MMM dd, yyyy 'at' h:mm aa")}
              </div>
            );
          } catch {
            return <div className='px-4'>{value}</div>;
          }
        },
      }),
      columnHelper.accessor('formName', {
        id: 'formName',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Form'
            disableSorting
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className='px-4'>
              <div className='text-sm font-medium'>{row.formName}</div>
              <div className='text-xs text-muted-foreground'>
                {formatFormTypeLabel(row.formType)}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('parent.firstName', {
        id: 'parent',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent'
            disableSorting
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className='px-4'>
              <div className='text-sm font-medium'>
                {row.parent.firstName} {row.parent.lastName}
                {row.child?.firstName ? (
                  <span className='font-normal text-muted-foreground'>
                    {' '}
                    (student: {row.child.firstName})
                  </span>
                ) : null}
              </div>
              <div className='text-xs text-muted-foreground'>
                {row.parent.email}
                {row.parent.phone ? ` · ${row.parent.phone}` : ''}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('sessionInterest', {
        id: 'sessionInterest',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Interest'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4 text-sm'>{info.getValue() ?? '—'}</div>
        ),
      }),
      columnHelper.accessor('sync.portalStatus', {
        id: 'portalStatus',
        meta: { mobileLabel: 'Portal Sync' },
        header: (info) => (
          <DataTableColumnHeader
            className='min-w-[110px] pl-4 text-black'
            column={info.column}
            title='Portal Sync'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='min-w-[110px] px-4'>
            <SyncStatusBadge status={info.row.original.sync.portalStatus} />
          </div>
        ),
      }),
      columnHelper.accessor('sync.ghlStatus', {
        id: 'crmStatus',
        meta: { mobileLabel: 'CRM Sync' },
        header: (info) => (
          <DataTableColumnHeader
            className='min-w-[140px] pl-4 text-black'
            column={info.column}
            title='CRM Sync'
            disableSorting
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          const contactId = row.sync.ghlContactId;
          return (
            <div className='flex min-w-[140px] flex-col gap-1 px-4'>
              <div className='flex items-center gap-2'>
                <SyncStatusBadge status={row.sync.ghlStatus} />
                {row.sync.ghlStatus === 'synced' && contactId ? (
                  <a
                    href={crmContactUrl(row.locationId, contactId)}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:underline'
                    aria-label='Open contact in CRM'
                  >
                    <ExternalLink className='size-3' />
                  </a>
                ) : null}
              </div>
              {row.sync.ghlResolution ? (
                <span className='text-[11px] text-muted-foreground'>
                  {formatCrmResolutionLabel(row.sync.ghlResolution)}
                </span>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor('sync.ghlLastError', {
        id: 'lastError',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Last error'
            disableSorting
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          const err = row.sync.ghlLastError ?? row.sync.portalLastError;
          return (
            <div className='max-w-[220px] truncate px-4 text-xs text-muted-foreground'>
              {err ?? '—'}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className='px-4' />,
        cell: (info) => {
          const row = info.row.original;
          const hasFailed =
            row.sync.ghlStatus === 'failed' ||
            row.sync.portalStatus === 'failed';
          return (
            <div className='flex items-center gap-1 px-4'>
              <button
                type='button'
                onClick={() => {
                  setSelected(row);
                  setIsDetailsOpen(true);
                }}
                className='cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                aria-label='View submission details'
              >
                <InfoIcon className='size-4' />
              </button>
              <button
                type='button'
                onClick={() => handleCopyContact(row)}
                className='cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                aria-label='Copy contact info'
              >
                <Copy className='size-4' />
              </button>
              {hasFailed ? (
                <button
                  type='button'
                  onClick={() => {
                    handleRetry(row);
                  }}
                  disabled={retryMutation.isPending}
                  className='cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50'
                  aria-label='Retry failed sync'
                >
                  <RefreshCw className='size-4' />
                </button>
              ) : null}
              {/* Skip with no channel only ever touches failed syncs — a
                  healthy pending channel is never cancelled from the row. To
                  skip a pending channel, use the per-channel action in the
                  details modal (requires confirmation). */}
              {hasFailed ? (
                <button
                  type='button'
                  onClick={() => {
                    handleSkip(row);
                  }}
                  disabled={skipMutation.isPending}
                  className='cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50'
                  aria-label='Skip failed sync'
                >
                  <SkipForward className='size-4' />
                </button>
              ) : null}
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatInStudioTz, retryMutation.isPending, skipMutation.isPending],
  );

  return (
    <>
      <Card>
        <CardContent className='pt-6'>
          {error && (
            <div className='mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive'>
              Error loading submissions:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </div>
          )}
          {!error && !isFetching && rows.length === 0 && (
            <div className='mb-6 rounded-md border p-4 text-center text-sm text-muted-foreground'>
              No form submissions found
            </div>
          )}
          <EnrollioTable
            ariaLabel='Form submissions'
            totalLabel='Submissions'
            filterHeader={
              <>
                <div className='relative w-[240px]'>
                  <Search className='absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    placeholder='Search name, email, phone'
                    type='search'
                    className='w-full pl-8'
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                  />
                </div>
                <Select
                  value={formTypeFilter}
                  onValueChange={(value) => {
                    setFormTypeFilter(value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                >
                  <SelectTrigger className='w-[220px]'>
                    <SelectValue placeholder='Form type' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All form types</SelectItem>
                    {availableFormTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {formatFormTypeLabel(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={syncStatusFilter}
                  onValueChange={(value) => {
                    setSyncStatusFilter(value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                >
                  <SelectTrigger className='w-[180px]'>
                    <SelectValue placeholder='Sync status' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All statuses</SelectItem>
                    {SYNC_STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DatePickerWithRange
                  date={dateRange}
                  setDate={(range) => {
                    setDateRange(range);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                />
              </>
            }
            toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
            columns={cols}
            data={rows}
            isUpdating={isFetching}
            sorting={sorting}
            onSortingChange={setSorting}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            pagination={pagination}
            onPaginationChange={setPagination}
            manualPagination
            rowCount={totalCount}
          />
        </CardContent>
      </Card>

      <FormSubmissionDetailsModal
        submission={selected}
        open={isDetailsOpen}
        onOpenChange={(open) => {
          setIsDetailsOpen(open);
          if (!open) setSelected(null);
        }}
      />
    </>
  );
}
